import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sun } from "lucide-react";
import { LineReveal } from "./anim";
import { useLead } from "./lead-context";
import { IMAGES } from "./site-data";

export const Hero = () => {
  const { openLead } = useLead();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.8]);

  return (
    <section id="top" ref={ref} className="relative h-screen w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Solar Garden on an ocean-view deck"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-sg-bg"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-sg-bg via-sg-bg/30 to-sg-bg/50" />

      <motion.div
        style={{ y: textY }}
        className="relative z-10 h-full mx-auto max-w-[1400px] px-5 sm:px-8 flex flex-col justify-end pb-16 sm:pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-sg-primary/40 bg-sg-bg/40 backdrop-blur px-4 py-1.5 text-xs tracking-[0.2em] uppercase text-sg-primary"
          data-testid="hero-badge"
        >
          <Sun size={13} /> Solar-powered raised garden
        </motion.div>

        <h1 className="font-display font-medium text-sg-fg tracking-tight text-[13vw] leading-[0.95] sm:text-7xl lg:text-8xl">
          <LineReveal
            lines={["Grow food.", "Generate power.", "Elevate your backyard."]}
            delay={0.45}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-sg-fg/75 font-light leading-relaxed"
        >
          A freestanding raised garden with an integrated solar canopy. It
          shades your plants while a built-in microinverter turns sunlight into
          usable AC power that can help offset your home's electricity — no roof
          panels required.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center gap-3"
        >
          <button
            onClick={() => openLead("quote")}
            data-testid="hero-quote-btn"
            className="inline-flex items-center gap-2 rounded-full bg-sg-primary px-7 py-3.5 text-sm font-medium text-sg-bg hover:bg-sg-fg transition-colors duration-300"
          >
            Request a Quote
          </button>
          <button
            onClick={() => openLead("waitlist")}
            data-testid="hero-waitlist-btn"
            className="inline-flex items-center gap-2 rounded-full border border-sg-fg/25 bg-sg-bg/20 backdrop-blur px-7 py-3.5 text-sm font-medium text-sg-fg hover:border-sg-fg/60 transition-colors duration-300"
          >
            Join the Waitlist
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-sg-fg/50"
      >
        <span>Scroll</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
        >
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
};
